import _get from 'lodash/get';
import { Attachment } from 'graphql-common';
import { Values } from '@lib/interfaces/form';

/**
 * Transforms an attachment from API format to form format for duplication.
 *
 * When duplicating an entity (widget template -> widget), attachments need to be
 * transformed so the backend can create a copy of the file rather than just
 * referencing the original.
 *
 * API format: { id, urls: { small, medium, original }, fileData: { url, data: {...} }, filename }
 * Form format: { filename, file: {...data}, url }
 *
 * The `file` property contains the file reference data that tells the backend
 * to duplicate the attachment. The `url` property is kept for preview purposes.
 *
 * @param attachment - Attachment in API format from GraphQL query
 * @returns Attachment in form format suitable for mutation, or null if invalid
 */
export default function transformAttachmentForDuplication(
  attachment: Attachment | Values | null | undefined,
): Values | null {
  if (!attachment) return null;

  const filename = _get(attachment, 'filename') as string | undefined;
  const fileData = _get(attachment, 'fileData') as
    | { url?: string; data?: Record<string, unknown> }
    | undefined;

  // fileData.data contains the file reference for duplication
  const data = fileData?.data;
  // fileData.url or urls.small/medium for preview
  const previewUrl =
    fileData?.url ||
    _get(attachment, 'urls.small') ||
    _get(attachment, 'urls.medium') ||
    _get(attachment, 'urls.original');

  // Must have data and filename to create a valid duplication reference
  if (!data || !filename) {
    return null;
  }

  return {
    filename,
    file: { ...data },
    url: previewUrl || undefined,
  };
}

/**
 * Transforms an array of attachments from API format to form format for duplication.
 *
 * @param attachments - Array of attachments in API format
 * @returns Array of attachments in form format (excludes any that couldn't be transformed)
 */
export function transformAttachmentsForDuplication(
  attachments: (Attachment | Values)[] | null | undefined,
): Values[] {
  if (!attachments || attachments.length === 0) return [];

  return attachments
    .map(transformAttachmentForDuplication)
    .filter((att): att is Values => att !== null);
}
